import React, { useCallback, useContext, useMemo } from 'react';
import classNames from 'classnames';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import { useTranslation } from 'react-i18next';
import {
  ASKING_PRICE_SELECT,
  DEAL_ROOM_TOTAL_PACKAGE_GST,
  EditDealRoomValues,
  PublishDealRoomValues,
  REVENUE_DESCRIPTION,
  STOCK_AT_VALUE,
} from 'models/DealRoom';
import CurrentUserContext from 'providers/CurrentUser/CurrentUser.context';
import { Form } from 'react-final-form';
import utils, {
  areArraysEqual,
  getSellingPortfolio,
  getShouldHasRevenueShares,
} from 'utils';
import api from 'api';
import { FormWrapper } from 'components/FormWrapper';
import arrayMutators from 'final-form-arrays';
import { MONEY_PROPS } from 'router/subrouters/DealRoomCreation/pages/CreateDealRoom/data';
import AuthContext from 'providers/Auth/Auth.context';
import { SUCCESS_MESSAGES } from 'request-responses';
import { validate } from 'router/subrouters/DealRoomCreation/pages/CreateDealRoom/utils';
import FirstStep from 'router/subrouters/DealRoomCreation/pages/CreateDealRoom/components/FirstStep';
import DealRoomFieldsRequiredVisibleInfoProvider from 'router/subrouters/DealRoomCreation/providers/DealRoomFieldsRequiredVisibleInfo';
import DealRoomContext from '../../providers/DealRoom/DealRoom.context';

import './EditDealRoom.styles.scss';

type EditDealRoomProps = {
  className?: string;
};

const EditDealRoom: React.FC<EditDealRoomProps> = (props) => {
  const { className } = props;

  const { dealRoom, colleagues } = useContext(DealRoomContext);

  const { popServerError, popSuccess } = useContext(PopNotificationsContext);
  const { navigateBack } = useContext(AuthContext);
  const { hasEnterprise } = useContext(CurrentUserContext);

  const colleaguesWithFilteredValues = useMemo(
    () =>
      colleagues.map(
        ({
          email,
          useMessaging,
          dataRoomAdmin,
          isOwner,
          sendNotifications,
          role,
          name,
        }) => ({
          email,
          useMessaging,
          dataRoomAdmin,
          sendNotifications,
          isOwner,
          role,
          name,
        }),
      ),
    [colleagues],
  );

  const { t } = useTranslation();

  const classes = classNames('edit-deal-room', className);

  const initialValues = useMemo(() => {
    const {
      priceGuide,
      totalPackageGST,
      revenueShares,
      stockAtValue,
      name,
      dealOutcome,
      otherDealOutcome,
      industry,
      location,
      amount,
      amountFrom,
      amountTo,
      averageStockValue,
      currency,
      totalPackage,
      companyName,
      projectedSalePrice,
      successFeeShare,
      successFeePercentage,
      franchisorFeePercentage,
    } = dealRoom || {};

    return {
      name,
      dealOutcome,
      otherDealOutcome,
      industry,
      location,
      currency,
      colleagues: colleaguesWithFilteredValues,
      companyName,
      successFeePercentage,
      franchisorFeePercentage,
      averageStockValue: utils.getDotedNumber(averageStockValue),
      totalPackage: utils.getDotedNumber(totalPackage),
      amount: utils.getDotedNumber(amount),
      amountFrom: utils.getDotedNumber(amountFrom),
      amountTo: utils.getDotedNumber(amountTo),
      projectedSalePrice: utils.getDotedNumber(projectedSalePrice),
      successFeeShare: utils.getDotedNumber(successFeeShare),
      priceGuide: priceGuide || ASKING_PRICE_SELECT.FIXED_PRICE,
      totalPackageGST: totalPackageGST || DEAL_ROOM_TOTAL_PACKAGE_GST.INCLUSIVE,
      stockAtValue: stockAtValue || STOCK_AT_VALUE.EXCLUDE_STOCK_AT_VALUE,
      revenueShares: revenueShares?.length ? revenueShares : [{}],
    };
  }, [colleaguesWithFilteredValues, dealRoom]);

  const onSubmit = useCallback(
    async (values: EditDealRoomValues) => {
      const {
        colleagues: formColleagues,
        revenueShares,
        companyName,
        stage,
        listingStage,
        successFeePercentage,
        ...rest
      } = values;
      const { revenueShares: initialRevenue } = initialValues;

      const formRevenueShares = revenueShares
        .filter(({ orderNumber, ...rest }) => Object.values(rest).length)
        .map(({ percentage, description, label, email, ...rest }, index) => ({
          ...rest,
          description,
          orderNumber: index,
          ...(description === REVENUE_DESCRIPTION.BROKERS_COMMISSION &&
            email && { email }),
          ...(description === REVENUE_DESCRIPTION.ADD_CUSTOM && { label }),
          ...(!!percentage && { percentage: +percentage }),
        }));

      const isRevenueEqual = areArraysEqual(initialRevenue, formRevenueShares);
      const isColleaguesEqual = areArraysEqual(colleagues, formColleagues);

      const moneyProps = utils.transformCurrencyPropertiesToNumber(
        rest,
        MONEY_PROPS,
      ) as PublishDealRoomValues;

      const hasRevenueShares =
        formRevenueShares?.length &&
        getShouldHasRevenueShares(
          moneyProps.projectedSalePrice,
          moneyProps.successFeeShare,
        );

      const newValues = {
        ...rest,
        ...moneyProps,
        companyName: companyName || null,
        totalPackage: moneyProps.totalPackage || null,
        projectedSalePrice: moneyProps.projectedSalePrice || null,
        successFeeShare: moneyProps.successFeeShare || null,
        successFeePercentage: successFeePercentage || null,
        ...(!isColleaguesEqual && {
          colleagues: (formColleagues || []).map(
            ({ isOwner, name, ...rest }) => rest,
          ),
        }),
        ...(!isRevenueEqual && {
          revenueShares: hasRevenueShares ? formRevenueShares : [],
        }),
      };

      try {
        await api.dealRoom.updateDealRoom(dealRoom?._id, newValues);

        popSuccess(t(SUCCESS_MESSAGES.EDIT_DEAL));
        navigateBack({ replace: true });
      } catch (error) {
        popServerError(error);
      }
    },
    [
      colleagues,
      dealRoom?._id,
      initialValues,
      navigateBack,
      popServerError,
      popSuccess,
      t,
    ],
  );

  return (
    <DealRoomFieldsRequiredVisibleInfoProvider
      dealRoomId={dealRoom?._id}
      dealRoom={dealRoom}
      canChangeRequired={false}
    >
      <Form
        initialValues={initialValues}
        onSubmit={onSubmit}
        validate={(values) => validate(values, hasEnterprise)}
        mutators={{ ...arrayMutators }}
        render={({ submitting, valid, dirty, handleSubmit }) => (
          <form onSubmit={handleSubmit} className={classes}>
            <FormWrapper
              title={t('editADeal')}
              disabledSubmit={submitting}
              isFloatingVisible={valid}
              isValid={valid}
              exitRoute={getSellingPortfolio(dealRoom?._id, 'deal')}
            >
              <FirstStep />
            </FormWrapper>
          </form>
        )}
      />
    </DealRoomFieldsRequiredVisibleInfoProvider>
  );
};

export default EditDealRoom;
